import React, { useMemo } from "react";
import Select, { MultiValue } from "react-select";
import { FormGroup } from "reactstrap";
import { selectStyles } from "../../../../../lib/utils/selectStyles";
import { Icon } from "../../../../shared/Icon/Icon";
import "./AdminNotificationsSetting.scss";
import { InputDescription } from "../../../../shared/InputDescription/InputDescription";
import useGrpcQuery from "../../../../../lib/hooks/useGrpcQuery";
import { useCustomerRequests } from "../../../../../api/grpc/customer/useCustomerRequests";
import {
  GetSystemNotificationRecipientsRequest,
  GetSystemNotificationRecipientsResponse,
} from "../../../../../api/grpc/customer/customer";

interface AdminNotificationOption {
  label: string;
  value: string;
}

interface Props {
  selectedAdminIds: string[];
  onChange: (adminIds: string[]) => void;
}

export const AdminNotificationsSetting = ({
  selectedAdminIds,
  onChange,
}: Props) => {
  const { getSystemNotificationRecipients } = useCustomerRequests();
  const {
    data: recipientsData,
    loading,
    error,
  } = useGrpcQuery<
    GetSystemNotificationRecipientsRequest,
    GetSystemNotificationRecipientsResponse
  >({
    method: getSystemNotificationRecipients,
    variables: {
      customerId: "",
    },
  });

  const adminNotificationOptions = useMemo(
    () =>
      Object.entries(recipientsData?.recipients || {})
        .map(([accountId, accountName]) => ({
          label: accountName,
          value: accountId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [recipientsData]
  );

  const selectedAdmins = useMemo(
    () =>
      adminNotificationOptions.filter((option) =>
        selectedAdminIds.includes(option.value)
      ),
    [adminNotificationOptions, selectedAdminIds]
  );

  const handleChange = (options: MultiValue<AdminNotificationOption>) => {
    if (!options) {
      onChange([]);
      return;
    }

    onChange(options.map((item) => item.value));
  };

  return (
    <FormGroup className="w-100 AdminNotificationsSetting">
      <label className="d-block">Admin notifications</label>

      <div className="AdminNotificationsSetting__container">
        <span className="label__main AdminNotificationsSetting__selectorLabel">
          Select which admins should receive system email notifications:
        </span>

        {error ? (
          <InputDescription title="Failed to load admin options" />
        ) : (
          <Select
            isMulti
            isClearable
            isSearchable={true}
            name="adminNotifications"
            id="adminNotifications"
            theme={selectStyles}
            options={adminNotificationOptions}
            isLoading={loading}
            value={selectedAdmins}
            onChange={handleChange}
          />
        )}

        <div className="AdminNotificationsSetting__info">
          <InputDescription description="Notifications include:" />
          <InputDescription description="-Device offline notifications" />
          <InputDescription description="-Calendar integration issues" />
          <InputDescription description="-Other system warnings" />
        </div>

        <div className="AdminNotificationsSetting__warning">
          <Icon icon="warning-triangle" />
          <InputDescription
            title="Owner role always receive system notifications and cannot be
            removed."
            titleColor="info"
          />
        </div>
      </div>
    </FormGroup>
  );
};
